/**
 * Orbit camera for the 3D view. Azimuth/elevation around a target point,
 * zoom, and pan: the target slides in its own horizontal plane, so the plan
 * moves under a camera that keeps its angle. A glide eases the camera over a
 * moment: its target to a point, so the view centers on it, its angle to look
 * from a chosen side, or all of it home. See View3D for the gesture wiring.
 */

import { add, cross, dot3d, lookAt, type Mat4, multiply, normalize, perspective, rayPlaneDistance, scale, sub, type Vec3 } from '../math3d';

const FOV = (50 * Math.PI) / 180;
/**
 * A rotate drag's ~110° vertical sweep: from nearly straight down (just shy
 * of 90° to avoid the look-at up-vector degeneracy) to 20° below the floor
 * plane. A drag that starts outside it, after a view from the view cube's
 * Top or Bottom, sweeps from where it starts instead (see `rotate`).
 */
const MIN_EL = (-20 * Math.PI) / 180;
const MAX_EL = (89 * Math.PI) / 180;
/** The farthest a chosen view looks down or up, the view cube's Top and Bottom: a tenth of a degree short of straight, where the look-at up vector still holds. */
export const VIEW_ELEVATION_LIMIT = (89.9 * Math.PI) / 180;
const WORLD_UP: Vec3 = [0, 1, 0];
/** The opening view: a pleasant 3/4 angle. */
export const HOME_ANGLE = { azimuth: Math.PI * 0.18, elevation: (55 * Math.PI) / 180 } as const;
/** How long a glide takes (ms). */
const GLIDE_MS = 450;

/** Ease in and out: a glide starts and stops gently. */
const smoothstep = (t: number): number => t * t * (3 - 2 * t);

/** How far a pan may carry the target from where `frame` put it, per plan axis (ft). */
export interface PanReach {
  x: number;
  z: number;
}

const clamp = (value: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, value));

/** The same turn as `angle`, the nearest way around from `from`. */
const nearestTurn = (from: number, angle: number): number => angle - 2 * Math.PI * Math.round((angle - from) / (2 * Math.PI));

/** Everything a glide can carry. */
interface CameraPose {
  target: Vec3;
  distance: number;
  azimuth: number;
  elevation: number;
}

/** A glide in progress: from where, to where, which parts of the pose it still carries, and when it started (set by the first `advance`). */
interface Glide {
  from: CameraPose;
  to: CameraPose;
  carries: Set<keyof CameraPose>;
  startMs: number | null;
}

export class Camera {
  target: Vec3 = [0, 0, 0];
  azimuth: number = HOME_ANGLE.azimuth;
  elevation: number = HOME_ANGLE.elevation;
  distance = 30;
  /** The framed model: where home is, how far away it fits, and how far a pan may go from it. */
  private home: { center: Vec3; radius: number; reach: PanReach } | null = null;
  private glide: Glide | null = null;

  /** Unit direction from target toward eye — also the "out of screen" axis. */
  viewerDir(): Vec3 {
    const ce = Math.cos(this.elevation);
    return [ce * Math.sin(this.azimuth), Math.sin(this.elevation), ce * Math.cos(this.azimuth)];
  }

  eye(): Vec3 {
    return add(this.target, scale(this.viewerDir(), this.distance));
  }

  /** View (look-at) matrix — also the eye-space basis the SSAO pass reconstructs in. */
  view(): Mat4 {
    return lookAt(this.eye(), this.target, WORLD_UP);
  }

  /** Projection matrix alone (lit mode needs view + proj split, not just their product). */
  proj(aspect: number): Mat4 {
    return perspective(FOV, aspect, 0.05, 1000);
  }

  viewProj(aspect: number): Mat4 {
    return multiply(this.proj(aspect), this.view());
  }

  /** World-space picking ray through a screen point (NDC, y up). */
  ray(ndcX: number, ndcY: number, aspect: number): { origin: Vec3; dir: Vec3 } {
    const origin = this.eye();
    const fwd = normalize(sub(this.target, origin));
    const right = normalize(cross(fwd, WORLD_UP));
    const up = cross(right, fwd);
    const t = Math.tan(FOV / 2);
    const dir = normalize(add(add(fwd, scale(right, ndcX * t * aspect)), scale(up, ndcY * t)));
    return { origin, dir };
  }

  /**
   * Rotation from a drag/scroll of (dx, dy) screen px — grabbing the tip of the
   * out-of-screen vector and moving it in the drag direction. The elevation
   * stays within the rotate sweep, widened to take in where it is now, so a
   * drag from the view cube's Top or Bottom turns on from there without a
   * jump and narrows back to the sweep as it comes in. Stops a glide's turn.
   */
  rotate(dxPx: number, dyPx: number): void {
    const k = 0.006;
    this.endGlideOf('azimuth', 'elevation');
    this.azimuth += dxPx * k;
    this.elevation = clamp(this.elevation - dyPx * k, Math.min(MIN_EL, this.elevation), Math.max(MAX_EL, this.elevation));
  }

  /** Look from `angle` at once, its elevation within the view cube's Top and Bottom. Stops a glide's turn. */
  setAngle({ azimuth, elevation }: { azimuth: number; elevation: number }): void {
    this.endGlideOf('azimuth', 'elevation');
    this.azimuth = azimuth;
    this.elevation = clamp(elevation, -VIEW_ELEVATION_LIMIT, VIEW_ELEVATION_LIMIT);
  }

  /** Zoom by `factor`. Stops a glide's zoom. */
  zoom(factor: number): void {
    this.endGlideOf('distance');
    this.distance = Math.max(2, Math.min(400, this.distance * factor));
  }

  /**
   * Pan: slide the target in its own horizontal plane so that the point now at
   * the view center lands (dxPx, dyPx) away on screen — grabbing the plan and
   * dragging it, exact for the target's plane at any camera angle. The target
   * stays within the framed reach. A gesture whose ray misses the plane
   * (looking along it, or up at it from below) moves nothing. Stops a
   * glide's move.
   */
  pan(dxPx: number, dyPx: number, viewWidthPx: number, viewHeightPx: number): void {
    this.endGlideOf('target');
    const ray = this.ray((2 * dxPx) / viewWidthPx, (-2 * dyPx) / viewHeightPx, viewWidthPx / viewHeightPx);
    const distance = rayPlaneDistance(ray.origin, ray.dir, this.target, WORLD_UP);
    if (distance === null) return;
    const hit = add(ray.origin, scale(ray.dir, distance));
    this.target = this.withinReach([2 * this.target[0] - hit[0], this.target[1], 2 * this.target[2] - hit[2]]);
  }

  /** Raise the camera and its target by `feet` (negative lowers them), no further than the framed model's radius from home. Stops a glide's move. */
  lift(feet: number): void {
    this.endGlideOf('target');
    const [x, y, z] = this.target;
    const lifted = y + feet;
    this.target = [x, this.home ? clamp(lifted, this.home.center[1] - this.home.radius, this.home.center[1] + this.home.radius) : lifted, z];
  }

  /**
   * Start gliding the target to `point` (kept within the pan reach), so the
   * view ends centered on it; `advance` moves it there over GLIDE_MS, easing
   * in and out. Rotate and zoom carry on during it; a pan, a lift, a new
   * framing or `stopGlide` stops it.
   */
  glideTo(point: Vec3): void {
    this.startGlide({ target: this.withinReach(point) });
  }

  /** Start gliding the camera's angle to `angle`, the nearest way around, its elevation within the view cube's Top and Bottom; target and distance stay. A rotate stops it. */
  glideToAngle({ azimuth, elevation }: { azimuth: number; elevation: number }): void {
    this.startGlide({ azimuth: nearestTurn(this.azimuth, azimuth), elevation: clamp(elevation, -VIEW_ELEVATION_LIMIT, VIEW_ELEVATION_LIMIT) });
  }

  /**
   * Start gliding the azimuth `quarterTurns` quarter turns on (positive raises
   * it: the camera circles right, so the model turns left) from where a turn
   * in flight would end, or from here: taps in quick succession add up, never
   * taking a shorter way back. Elevation, target and distance stay.
   */
  glideTurn(quarterTurns: number): void {
    const from = this.glide?.carries.has('azimuth') ? this.glide.to.azimuth : this.azimuth;
    this.startGlide({ azimuth: from + (quarterTurns * Math.PI) / 2 });
  }

  /** Start gliding back to the opening view of the framed model: home target, fit distance, the 3/4 angle. */
  glideHome(): void {
    this.startGlide({
      azimuth: nearestTurn(this.azimuth, HOME_ANGLE.azimuth),
      elevation: HOME_ANGLE.elevation,
      ...(this.home && { target: this.home.center, distance: this.fitDistance(this.home.radius) }),
    });
  }

  /**
   * Glide toward `to`. Whatever a glide in flight was still carrying and
   * `to` leaves out glides on toward its own end, eased afresh from here.
   */
  private startGlide(to: Partial<CameraPose>): void {
    const from = this.pose();
    const glide = this.glide;
    const pending: Partial<CameraPose> = glide ? Object.fromEntries([...glide.carries].map(part => [part, glide.to[part]])) : {};
    const destination = { ...pending, ...to };
    const end = { ...from, ...destination };
    this.glide = { from, to: end, carries: new Set(Object.keys(destination) as (keyof CameraPose)[]), startMs: null };
  }

  /** Stop the parts of a glide the user has taken over; a glide carrying nothing more is over. */
  private endGlideOf(...parts: (keyof CameraPose)[]): void {
    for (const part of parts) this.glide?.carries.delete(part);
    if (this.glide?.carries.size === 0) this.glide = null;
  }

  /** Stop a glide where it is, so the camera holds still (a gadget drag needs it to). */
  stopGlide(): void {
    this.glide = null;
  }

  /** Move a glide on to `nowMs`; false when there is no glide. The first call after a glide starts its clock. */
  advance(nowMs: number): boolean {
    const glide = this.glide;
    if (!glide) return false;
    glide.startMs ??= nowMs;
    const t = Math.min(1, (nowMs - glide.startMs) / GLIDE_MS);
    const eased = smoothstep(t);
    const toward = (from: number, to: number): number => t >= 1 ? to : from + (to - from) * eased;
    const { from, to, carries } = glide;
    if (carries.has('target')) this.target = t >= 1 ? [...to.target] : add(from.target, scale(sub(to.target, from.target), eased));
    if (carries.has('distance')) this.distance = toward(from.distance, to.distance);
    if (carries.has('azimuth')) this.azimuth = toward(from.azimuth, to.azimuth);
    if (carries.has('elevation')) this.elevation = toward(from.elevation, to.elevation);
    if (t >= 1) this.glide = null;
    return true;
  }

  private pose(): CameraPose {
    return { target: [...this.target], distance: this.distance, azimuth: this.azimuth, elevation: this.elevation };
  }

  private withinReach([x, y, z]: Vec3): Vec3 {
    if (!this.home) return [x, y, z];
    const { center, reach } = this.home;
    return [clamp(x, center[0] - reach.x, center[0] + reach.x), y, clamp(z, center[2] - reach.z, center[2] + reach.z)];
  }

  /** Feet per CSS pixel at a world point's depth (the gadget sizes itself in pixels). */
  feetPerPixelAt(point: Vec3, viewportHeightPx: number): number {
    const depth = Math.max(0.05, dot3d(sub(point, this.eye()), scale(this.viewerDir(), -1)));
    return (2 * Math.tan(FOV / 2) * depth) / viewportHeightPx;
  }

  /**
   * On-screen size of one model foot on the plane through the camera target
   * (≈ the room center), in the same pixel units as `viewportHeightPx`.
   * Drives the background grid so its scale tracks the zoom level.
   */
  pixelsPerFoot(viewportHeightPx: number): number {
    return viewportHeightPx / (2 * Math.tan(FOV / 2) * this.distance);
  }

  /**
   * Frame a plan-space model: center the target and set a distance that fits
   * it. This framing becomes home, and `reach` is how far a pan may carry the
   * target from it.
   */
  frame(center: Vec3, radius: number, reach: PanReach = { x: 0, z: 0 }): void {
    this.setHome(center, radius, reach);
    this.stopGlide();
    this.target = [...center];
    this.distance = this.fitDistance(radius);
  }

  /** How far away a model of `radius` fits the view. */
  private fitDistance(radius: number): number {
    return Math.max(8, (radius * 1.15) / Math.tan(FOV / 2));
  }

  /** Remember a framing without moving the camera: a scene update that keeps the user's view. */
  setHome(center: Vec3, radius: number, reach: PanReach): void {
    this.home = { center: [...center], radius, reach };
  }
}
